export type AdaptiveBatchOptions = {
  /** Base debounce delay (ms) when traffic is low */
  baseDelay?: number
  /** Max debounce delay (ms) under heavy load */
  maxDelay?: number
  /** How many events/flush cycle counts as "busy" */
  busyThreshold?: number
  /** How quickly to decay back to baseDelay (multiplier per quiet flush, e.g. 0.5) */
  decayFactor?: number
}

/**
 * We want to protect the triple store if there are a lot of events.
 * This utility helps to debounce events that have the same key
 *
 * Co-authored-by: Claude
 */
export class AdaptiveBatcher<K, V> {
  private pending = new Map<K, V>()
  private timer: ReturnType<typeof setTimeout> | null = null
  private _currentDelay: number

  private readonly baseDelay: number
  private readonly maxDelay: number
  private readonly busyThreshold: number
  private readonly decayFactor: number

  /**
   * @param onFlush - Called with the accumulated batch once the debounce window
   *                  expires. Receives a `Map<K, V>` containing one entry per unique key.
   * @param options - Optional tuning parameters for the adaptive behaviour.
   */
  constructor(
    private readonly onFlush: (batch: Map<K, V>) => void,
    options: AdaptiveBatchOptions = {}
  ) {
    this.baseDelay     = options.baseDelay     ?? 50
    this.maxDelay      = options.maxDelay      ?? 2000
    this.busyThreshold = options.busyThreshold ?? 5
    this.decayFactor   = options.decayFactor   ?? 0.5
    this._currentDelay = this.baseDelay
  }

  /**
   * Add or overwrite a keyed entry
   * @param key - Deduplication key; a second push with the same key replaces the first.
   * @param value - Value to associate with the key.
   */
  push(key: K, value: V): void {
    this.pending.set(key, value)
    this.schedule()
  }

  /** Force an immediate flush */
  flush(): void {
    this.clearTimer()
    this.doFlush()
  }

  /**
   * Cancel any pending flush without flushing
   * Call this during cleanup (e.g. in a `useEffect` teardown).
   */
  cancel(): void {
    this.clearTimer()
    this.pending.clear()
  }

  private schedule(): void {
    if (this.timer !== null) return
    this.timer = setTimeout(() => {
      this.timer = null
      this.doFlush()
    }, this._currentDelay)
  }

  private doFlush(): void {
    const batch = this.pending
    this.pending = new Map()

    this._currentDelay = batch.size >= this.busyThreshold
      ? Math.min(this._currentDelay * 2, this.maxDelay)
      : Math.max(this._currentDelay * (1 - this.decayFactor), this.baseDelay)

    this.onFlush(batch)
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }
}
